import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ComplianceTrendChart } from "@/components/ComplianceTrendChart";
import { AiNote } from "@/components/AiNote";
import { api, apiError, fmtDay, fmtLakhValue, fmtPct } from "@/lib/api";

const Fact = ({ label, value, colour }) => (
  <div className="bg-card p-3">
    <div className="label-caps">{label}</div>
    <div
      className="font-data mt-1 text-lg font-bold tabular-nums"
      style={colour ? { color: colour } : undefined}
    >
      {value}
    </div>
  </div>
);

/** The compliance trend of one usecase, opened from an odometer. Read-only — this is the picture;
 *  the figures behind it are filled on the Compliance Data screen. */
export const ComplianceTrendDialog = ({ projectId, onClose }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    if (!projectId) return;
    api
      .get(`/compliance/trend/${projectId}`)
      .then((r) => setData(r.data))
      .catch((e) => {
        toast.error(apiError(e));
        onClose?.();
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const card = data?.card;

  return (
    <Dialog open={!!projectId} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent
        className="max-h-[92vh] max-w-4xl overflow-y-auto rounded-none bg-card p-0"
        data-testid="compliance-trend-dialog"
      >
        {!data ? (
          <div className="flex h-56 items-center justify-center">
            <DialogTitle className="sr-only">Opening the compliance trend</DialogTitle>
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            <DialogHeader className="border-b border-border p-6 pb-5 text-left">
              <div className="font-data flex flex-wrap items-center gap-2 text-[10px] uppercase tracking-wider text-muted-foreground">
                <span className="border border-border px-1.5 py-0.5">{card.plant}</span>
                <span className="bg-primary px-1.5 py-0.5 text-primary-foreground">
                  {card.phase || card.status || "—"}
                </span>
                <span>{card.code}</span>
              </div>
              <DialogTitle className="mt-2 font-display text-2xl font-black leading-tight tracking-tight">
                {card.title}
              </DialogTitle>
              <p className="mt-1 text-[11px] text-muted-foreground">
                {card.kpi.name || "KPI not named"}
                {card.kpi.unit ? ` · ${card.kpi.unit}` : ""} · measured from{" "}
                {fmtDay(card.assessment_from)} · {card.compliance.days} day(s) on record
              </p>
            </DialogHeader>

            <div className="grid gap-px bg-border sm:grid-cols-4">
              <Fact label="Yesterday" value={fmtPct(card.compliance.yesterday_pct)} />
              <Fact label="Today" value={fmtPct(card.compliance.today_pct)} />
              <Fact
                label="Cumulative"
                value={fmtPct(card.compliance.cumulative_pct)}
                colour="hsl(199 89% 48%)"
              />
              <Fact
                label="Impact so far"
                value={fmtLakhValue(card.impact.total_lakh)}
                colour="hsl(var(--healthy))"
              />
            </div>

            <div className="p-5">
              {data.series.length ? (
                <ComplianceTrendChart series={data.series} testId="trend-chart" />
              ) : (
                <p className="py-10 text-center text-sm text-muted-foreground">
                  No day has been filled for this usecase yet. The first reading is recorded on the
                  Compliance Data screen.
                </p>
              )}
              <p className="font-data mt-2 text-[10px] uppercase tracking-wider text-muted-foreground">
                Target {card.kpi.target ?? "not declared"} · Baseline{" "}
                {card.kpi.baseline ?? "not declared"} · {card.compliance.days_at_100} day(s) at
                100%
              </p>
              {card.remark && (
                <p className="mt-3 border-l-2 border-border pl-3 text-[11px] leading-relaxed text-muted-foreground">
                  {card.remark}
                </p>
              )}
              <div className="mt-4">
                <AiNote
                  kind="deepdive"
                  params={{ project_id: card.project_id }}
                  cta="Explain these readings"
                  blurb="How this usecase is running, what the numbers say and what to watch."
                  testId={`ai-note-trend-${card.code}`}
                />
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
